import React, { useState, useEffect } from "react";
import Select from "../../components/form/Select";
import Button from "../../components/common/Button";
import Input from "../../components/form/input/InputField";
import api from "../../utills/api";
import { GET_ALL_CATEGORIES, ADD_CATEGORIES, UPDATE_CATEGORIES, GET_ALL_SUBCATEGORIES, ADD_SUBCATEGORIES, UPDATE_SUBCATEGORIES, GET_ALL_MODELS, ADD_MODELS, UPDATE_MODELS } from "../../utills/endpoint";

interface Category {
  _id: string;
  name: string;
}

interface Subcategory {
  _id: string;
  name: string;
  categoryId?: string;
}

interface TextureCategory {
  _id: string;
  name: string;
  description?: string;
}

interface Texture {
  _id: string;
  textureId: string;
  textureCategory: string;
  textureUrl: string;
  textureType: string;
  textureName?: string;
}

interface Color {
  colorName: string;
  colorCode: string;
}

interface Model {
  _id: string;
  name: string;
  shape: string;
  model_url?: string;
  image2d_url?: string;
  height: number;
  depth: number;
  width: number;
  textureavailable: string[];
  colorsavailable: string[];
  category: string;
  subcategory: string;
  customizable: boolean;
  price: number;
  customizableProduct?: boolean;
  modelCustomisable?: string[]; // Array of model IDs for customizable models
}

interface CustomizableModel {
  _id?: string;
  name: string;
  shape: string;
  model_url?: string;
  image2d_url?: string;
  height: number;
  depth: number;
  width: number;
  textureavailable: string[];
  colorsavailable: string[];
  price: number;
  customizableProduct: boolean;
  customizable: boolean;
}

const ModelAdmin: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>("");
  const [editCategoryName, setEditCategoryName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // Subcategory states
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>("");
  const [isSubcategoryModalOpen, setIsSubcategoryModalOpen] = useState<boolean>(false);
  const [isSubcategoryEditModalOpen, setIsSubcategoryEditModalOpen] = useState<boolean>(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState<string>("");
  const [editSubcategoryName, setEditSubcategoryName] = useState<string>("");
  const [subcategoryLoading, setSubcategoryLoading] = useState<boolean>(false);
  const [subcategoryError, setSubcategoryError] = useState<string>("");

  // Model states
  const [models, setModels] = useState<Model[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [isModelModalOpen, setIsModelModalOpen] = useState<boolean>(false);
  
  // Texture states
  const [textureCategories, setTextureCategories] = useState<TextureCategory[]>([]);
  const [textures, setTextures] = useState<Texture[]>([]);
  const [selectedTextureCategory, setSelectedTextureCategory] = useState<string>("");
  const [isModelEditModalOpen, setIsModelEditModalOpen] = useState<boolean>(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [modelLoading, setModelLoading] = useState<boolean>(false);
  const [modelError, setModelError] = useState<string>("");
  
  // Model form states
  const [modelForm, setModelForm] = useState({
    name: "",
    shape: "",
    model_url: "",
    image2d_url: "",
    height: "",
    depth: "",
    width: "",
    textureavailable: [] as string[],
    colorsavailable: [] as string[],
    customizable: false,
    price: ""
  });

  // File upload states
  const [modelFile, setModelFile] = useState<File | null>(null);
  const [image2dFile, setImage2dFile] = useState<File | null>(null);

  // Customizable models states
  const [customizableModels, setCustomizableModels] = useState<CustomizableModel[]>([]);
  const [isCustomizableModalOpen, setIsCustomizableModalOpen] = useState<boolean>(false);
  const [customizableForm, setCustomizableForm] = useState({
    name: "",
    shape: "",
    model_url: "",
    image2d_url: "",
    height: "",
    depth: "",
    width: "",
    textureavailable: [] as string[],
    colorsavailable: [] as string[],
    price: ""
  });
  const [customizableModelFile, setCustomizableModelFile] = useState<File | null>(null);
  const [customizableImage2dFile, setCustomizableImage2dFile] = useState<File | null>(null);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
    fetchTextureCategories();
    fetchTextures();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (selectedCategory) {
      fetchSubcategories();
    }
  }, [selectedCategory]);

  // Fetch models when subcategory changes
  useEffect(() => {
    if (selectedCategory && selectedSubcategory) {
      fetchModels();
    }
  }, [selectedCategory, selectedSubcategory]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get(GET_ALL_CATEGORIES);
      console.log(response.data);
      if (response.data && response.data.categories) {
        setCategories(response.data.categories);
        // Set first category as default selected
        if (response.data.categories.length > 0) {
          setSelectedCategory(response.data.categories[0]._id);
        }
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async () => {
    try {
      setSubcategoryLoading(true);
      const response = await api.get(`${GET_ALL_SUBCATEGORIES}${selectedCategory}`);
      console.log("Subcategories response:", response.data);
      if (response.data && response.data.subcategories) {
        setSubcategories(response.data.subcategories);
        // Set first subcategory as default selected
        if (response.data.subcategories.length > 0) {
          setSelectedSubcategory(response.data.subcategories[0]._id);
        }
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setSubcategoryError("Failed to fetch subcategories");
    } finally {
      setSubcategoryLoading(false);
    }
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setSelectedSubcategory(""); // Reset subcategory selection
  };

  const handleSubcategoryChange = (value: string) => {
    setSelectedSubcategory(value);
  };

  const fetchModels = async () => {
    try {
      setModelLoading(true);
      const response = await api.get(`${GET_ALL_MODELS}?category=${selectedCategory}&subcategory=${selectedSubcategory}`);
      console.log("Models response:", response.data);
      console.log("Models:", response.data.models);
      if (response.data && response.data.data.models) {
        setModels(response.data.data.models);
        // Set first model as default selected
        if (response.data.data.models.length > 0) {
          setSelectedModel(response.data.data.models[0]._id);
        } else {
          setSelectedModel("");
        }
      }
      console.log("Models:", models);
    } catch (error) {
      console.error("Error fetching models:", error);
      setModelError("Failed to fetch models");
    } finally {
      setModelLoading(false);
    }
  };

  const fetchTextureCategories = async () => {
    try {
      const response = await api.get('/admin/api/texture-category');
      console.log("Texture categories response:", response.data);
      if (response.data && response.data.categories) {
        setTextureCategories(response.data.categories);
        // Set first category as default
        if (response.data.categories.length > 0) {
          setSelectedTextureCategory(response.data.categories[0]._id);
        }
      }
    } catch (error) {
      console.error("Error fetching texture categories:", error);
    }
  };

  const fetchTextures = async () => {
    try {
      const response = await api.get('/admin/api/texture/all_textures');
      console.log("Textures response:", response.data);
      if (response.data && response.data.textures) {
        setTextures(response.data.textures);
      }
    } catch (error) {
      console.error("Error fetching textures:", error);
    }
  };

  const getTexturesByCategory = (categoryId: string) => {
    return textures.filter(texture => texture.textureCategory === categoryId);
  };

  const getTextureById = (textureId: string) => {
    return textures.find(texture => texture._id === textureId);
  };

  const handleAddCategory = () => {
    setIsModalOpen(true);
  };

  const handleEditCategory = () => {
    if (!selectedCategory) {
      setError("Please select a category to edit");
      return;
    }
    const category = categories.find(cat => cat._id === selectedCategory);
    if (category) {
      setEditCategoryName(category.name);
      setIsEditModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewCategoryName("");
    setError("");
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditCategoryName("");
    setError("");
  };

  // Subcategory handlers
  const handleAddSubcategory = () => {
    setIsSubcategoryModalOpen(true);
  };

  const handleEditSubcategory = () => {
    if (!selectedSubcategory) {
      setSubcategoryError("Please select a subcategory to edit");
      return;
    }
    const subcategory = subcategories.find(sub => sub._id === selectedSubcategory);
    if (subcategory) {
      setEditSubcategoryName(subcategory.name);
      setIsSubcategoryEditModalOpen(true);
    }
  };

  const handleCloseSubcategoryModal = () => {
    setIsSubcategoryModalOpen(false);
    setNewSubcategoryName("");
    setSubcategoryError("");
  };

  const handleCloseSubcategoryEditModal = () => {
    setIsSubcategoryEditModalOpen(false);
    setEditSubcategoryName("");
    setSubcategoryError("");
  };

  // Model handlers
  const handleAddModel = () => {
    setModelForm({
      name: "",
      shape: "",
      model_url: "",
      image2d_url: "",
      height: "",
      depth: "",
      width: "",
      textureavailable: [],
      colorsavailable: [],
      customizable: false,
      price: ""
    });
    setModelFile(null);
    setImage2dFile(null);
    setIsModelModalOpen(true);
  };

  const handleEditModel = (model: Model) => {
    setEditingModel(model);
    setModelForm({
      name: model.name,
      shape: model.shape,
      model_url: model.model_url || "",
      image2d_url: model.image2d_url || "",
      height: model.height.toString(),
      depth: model.depth.toString(),
      width: model.width.toString(),
      textureavailable: model.textureavailable,
      colorsavailable: model.colorsavailable,
      customizable: model.customizable || false,
      price: model.price?.toString() || ""
    });

    // If model is customizable and has modelCustomisable IDs, load those models
    if (model.customizable && model.modelCustomisable && model.modelCustomisable.length > 0) {
      // Find all models that match the IDs in modelCustomisable
      const customizableModelData = model.modelCustomisable
        .map(id => models.find(m => m._id === id))
        .filter(Boolean) as Model[];
      
      // Convert to CustomizableModel format
      const customizableModelsData: CustomizableModel[] = customizableModelData.map(model => ({
        _id: model._id,
        name: model.name,
        shape: model.shape,
        model_url: model.model_url,
        image2d_url: model.image2d_url,
        height: model.height,
        depth: model.depth,
        width: model.width,
        textureavailable: model.textureavailable,
        colorsavailable: model.colorsavailable,
        price: model.price,
        customizableProduct: true,
        customizable: false
      }));
      
      setCustomizableModels(customizableModelsData);
    } else {
      setCustomizableModels([]);
    }

    setIsModelEditModalOpen(true);
  };

  const handleCloseModelModal = () => {
    setIsModelModalOpen(false);
    setModelForm({
      name: "",
      shape: "",
      model_url: "",
      image2d_url: "",
      height: "",
      depth: "",
      width: "",
      textureavailable: [],
      colorsavailable: [],
      customizable: false,
      price: ""
    });
    setModelFile(null);
    setImage2dFile(null);
    setModelError("");
  };

  const handleCloseModelEditModal = () => {
    setIsModelEditModalOpen(false);
    setEditingModel(null);
    setModelForm({
      name: "",
      shape: "",
      model_url: "",
      image2d_url: "",
      height: "",
      depth: "",
      width: "",
      textureavailable: [],
      colorsavailable: [],
      customizable: false,
      price: ""
    });
    setCustomizableModels([]); // Clear customizable models
    setModelError("");
  };

  // Customizable model handlers
  const handleAddCustomizableModel = () => {
    setCustomizableForm({
      name: "",
      shape: "",
      model_url: "",
      image2d_url: "",
      height: "",
      depth: "",
      width: "",
      textureavailable: [],
      colorsavailable: [],
      price: ""
    });
    setCustomizableModelFile(null);
    setCustomizableImage2dFile(null);
    setIsCustomizableModalOpen(true);
  };

  const handleCloseCustomizableModal = () => {
    setIsCustomizableModalOpen(false);
    setCustomizableForm({
      name: "",
      shape: "",
      model_url: "",
      image2d_url: "",
      height: "",
      depth: "",
      width: "",
      textureavailable: [],
      colorsavailable: [],
      price: ""
    });
    setCustomizableModelFile(null);
    setCustomizableImage2dFile(null);
    setModelError("");
  };

  const handleRemoveCustomizableModel = async (index: number) => {
    const modelToRemove = customizableModels[index];
    
    // If the model has an _id, delete it from backend
    if (modelToRemove._id) {
      try {
        await api.delete(`${UPDATE_MODELS}/${modelToRemove._id}`);
      } catch (error) {
        console.error("Error deleting customizable model:", error);
        setModelError("Failed to delete customizable model");
        return;
      }
    }
    
    // Remove from local state
    setCustomizableModels(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitCategory = async () => {
    if (!newCategoryName.trim()) {
      setError("Category name is required");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const response = await api.post(ADD_CATEGORIES, {
        name: newCategoryName.trim()
      });

      if (response.data) {
        // Refresh categories list
        await fetchCategories();
        handleCloseModal();
      }
    } catch (error: any) {
      console.error("Error adding category:", error);
      setError(error.response?.data?.message || "Failed to add category");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEditCategory = async () => {
    if (!editCategoryName.trim()) {
      setError("Category name is required");
      return;
    }

    try {
      setLoading(true);
      setError("");
      console.log(selectedCategory);
      const response = await api.put(`${UPDATE_CATEGORIES}`, {
        id: selectedCategory,
        name: editCategoryName.trim()
      });

      if (response.data) {
        // Refresh categories list
        await fetchCategories();
        handleCloseEditModal();
      }
    } catch (error: any) {
      console.error("Error updating category:", error);
      setError(error.response?.data?.message || "Failed to update category");
    } finally {
      setLoading(false);
    }
  };

  // Subcategory submit functions
  const handleSubmitSubcategory = async () => {
    if (!newSubcategoryName.trim()) {
      setSubcategoryError("Subcategory name is required");
      return;
    }

    try {
      setSubcategoryLoading(true);
      setSubcategoryError("");
      
      const response = await api.post(ADD_SUBCATEGORIES, {
        name: newSubcategoryName.trim(),
        category_id: selectedCategory
      });

      if (response.data) {
        // Refresh subcategories list
        await fetchSubcategories();
        handleCloseSubcategoryModal();
      }
    } catch (error: any) {
      console.error("Error adding subcategory:", error);
      setSubcategoryError(error.response?.data?.message || "Failed to add subcategory");
    } finally {
      setSubcategoryLoading(false);
    }
  };

  const handleSubmitEditSubcategory = async () => {
    if (!editSubcategoryName.trim()) {
      setSubcategoryError("Subcategory name is required");
      return;
    }

    try {
      setSubcategoryLoading(true);
      setSubcategoryError("");
      
      const response = await api.put(`${UPDATE_SUBCATEGORIES}`, {
        id: selectedSubcategory,
        name: editSubcategoryName.trim()
      });

      if (response.data) {
        // Refresh subcategories list
        await fetchSubcategories();
        handleCloseSubcategoryEditModal();
      }
    } catch (error: any) {
      console.error("Error updating subcategory:", error);
      setSubcategoryError(error.response?.data?.message || "Failed to update subcategory");
    } finally {
      setSubcategoryLoading(false);
    }
  };

  // Model submit functions
  const handleSubmitModel = async () => {
    if (!modelForm.name.trim() || !modelForm.shape.trim() || !modelForm.height || !modelForm.depth || !modelForm.width) {
      setModelError("Name, shape, height, depth, and width are required");
      return;
    }

    try {
      setModelLoading(true);
      setModelError("");
      
      // Create FormData for file upload
      const formData = new FormData();
      
      // Add the model data as JSON string
      const modelData = {
        name: modelForm.name.trim(),
        shape: modelForm.shape.trim(),
        height: parseFloat(modelForm.height),
        depth: parseFloat(modelForm.depth),
        width: parseFloat(modelForm.width),
        textureavailable: modelForm.textureavailable,
        colorsavailable: modelForm.colorsavailable,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        customizable: modelForm.customizable,
        price: parseFloat(modelForm.price) || 0,
        modelCustomisable: customizableModels.map(model => model._id).filter(Boolean), // Array of customizable model IDs
        customizableProduct: false
      };
      
      formData.append('data', JSON.stringify(modelData));
      
      // Add files if they exist
      if (modelFile) {
        formData.append('modelFile', modelFile);
      }
      if (image2dFile) {
        formData.append('image2d', image2dFile);
      }
      
      const response = await api.post(ADD_MODELS, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        // Refresh models list
        await fetchModels();
        handleCloseModelModal();
      }
    } catch (error: any) {
      console.error("Error adding model:", error);
      setModelError(error.response?.data?.message || "Failed to add model");
    } finally {
      setModelLoading(false);
    }
  };

  const handleSubmitEditModel = async () => {
    if (!modelForm.name.trim() || !modelForm.shape.trim() || !modelForm.height || !modelForm.depth || !modelForm.width) {
      setModelError("Name, shape, height, depth, and width are required");
      return;
    }

    try {
      setModelLoading(true);
      setModelError("");
      
      const response = await api.put(`${UPDATE_MODELS}/${editingModel?._id}`, {
        name: modelForm.name.trim(),
        shape: modelForm.shape.trim(),
        model_url: modelForm.model_url.trim(),
        image2d_url: modelForm.image2d_url.trim(),
        height: parseFloat(modelForm.height),
        depth: parseFloat(modelForm.depth),
        width: parseFloat(modelForm.width),
        textureavailable: modelForm.textureavailable,
        colorsavailable: modelForm.colorsavailable,
        customizable: modelForm.customizable,
        price: parseFloat(modelForm.price) || 0,
        modelCustomisable: customizableModels.map(model => model._id).filter(Boolean), // Array of customizable model IDs
        customizableProduct: false
      });

      if (response.data) {
        // Refresh models list
        await fetchModels();
        handleCloseModelEditModal();
      }
    } catch (error: any) {
      console.error("Error updating model:", error);
      setModelError(error.response?.data?.message || "Failed to update model");
    } finally {
      setModelLoading(false);
    }
  };

  const handleSubmitCustomizableModel = async () => {
    if (!customizableForm.name.trim() || !customizableForm.shape.trim() || !customizableForm.height || !customizableForm.depth || !customizableForm.width) {
      setModelError("Name, shape, height, depth, and width are required");
      return;
    }

    try {
      setModelLoading(true);
      setModelError("");
      
      // Create FormData for file upload
      const formData = new FormData();
      
      // Add the model data as JSON string
      const modelData = {
        name: customizableForm.name.trim(),
        shape: customizableForm.shape.trim(),
        height: parseFloat(customizableForm.height),
        depth: parseFloat(customizableForm.depth),
        width: parseFloat(customizableForm.width),
        textureavailable: customizableForm.textureavailable,
        colorsavailable: customizableForm.colorsavailable,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        customizableProduct: true, // Set to true for customizable models
        price: parseFloat(customizableForm.price) || 0,
        customizable: false
      };
      
      formData.append('data', JSON.stringify(modelData));
      
      // Add files if they exist
      if (customizableModelFile) {
        formData.append('modelFile', customizableModelFile);
      }
      if (customizableImage2dFile) {
        formData.append('image2d', customizableImage2dFile);
      }
      
      const response = await api.post(ADD_MODELS, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        // Add the new customizable model to the list with its ID
        const newCustomizableModel: CustomizableModel = {
          _id: response.data.data?._id || response.data._id,
          name: customizableForm.name.trim(),
          shape: customizableForm.shape.trim(),
          height: parseFloat(customizableForm.height),
          depth: parseFloat(customizableForm.depth),
          width: parseFloat(customizableForm.width),
          textureavailable: customizableForm.textureavailable,
          colorsavailable: customizableForm.colorsavailable,
          price: parseFloat(customizableForm.price) || 0,
          customizableProduct: true,
          customizable: false
        };
        
        setCustomizableModels(prev => [...prev, newCustomizableModel]);
        handleCloseCustomizableModal();
      }
    } catch (error: any) {
      console.error("Error adding customizable model:", error);
      setModelError(error.response?.data?.message || "Failed to add customizable model");
    } finally {
      setModelLoading(false);
    }
  };

  // Convert categories to dropdown options
  const categoryOptions = categories.map(category => ({
    value: category._id,
    label: category.name
  }));

  // Convert subcategories to dropdown options
  const subcategoryOptions = subcategories.map(subcategory => ({
    value: subcategory._id,
    label: subcategory.name
  }));

  return (
    <div className="p-6 w-full max-w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Model Admin
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage categories and models
        </p>
      </div>

      {/* Category Selection Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Category Selection
          </h2>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Category
            </label>
            <Select
              options={categoryOptions}
              placeholder={selectedCategory ? categories.find(cat => cat._id === selectedCategory)?.name : "Select a category"}
              onChange={handleCategoryChange}
              defaultValue={selectedCategory}
              className="w-full"
            />
          </div>
          
          <div className="flex items-end gap-3">
            <Button
              onClick={handleAddCategory}
              variant="primary"
              size="md"
              startIcon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 5V19M5 12H19"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            >
              Add Category
            </Button>
            <Button
              onClick={handleEditCategory}
              variant="secondary"
              size="md"
              disabled={!selectedCategory}
              startIcon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="m18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            >
              Edit Category
            </Button>
          </div>
        </div>

       
      </div>

      {/* Subcategory Selection Section - Only show when category is selected */}
      {selectedCategory && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Subcategory Selection
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Category: {categories.find(cat => cat._id === selectedCategory)?.name}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Subcategory
              </label>
              <Select
                options={subcategoryOptions}
                placeholder={selectedSubcategory ? subcategories.find(sub => sub._id === selectedSubcategory)?.name : "Select a subcategory"}
                onChange={handleSubcategoryChange}
                defaultValue={selectedSubcategory}
                className="w-full"
              />
            </div>
            
            <div className="flex items-end gap-3">
              <Button
                onClick={handleAddSubcategory}
                variant="primary"
                size="md"
                startIcon={
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              >
                Add Subcategory
              </Button>
              <Button
                onClick={handleEditSubcategory}
                variant="secondary"
                size="md"
                disabled={!selectedSubcategory}
                startIcon={
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="m18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              >
                Edit Subcategory
              </Button>
            </div>
          </div>

          {subcategoryError && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{subcategoryError}</p>
            </div>
          )}

          {subcategoryLoading && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-600 dark:text-blue-400">Loading subcategories...</p>
            </div>
          )}
        </div>
      )}

      {/* Models Section - Only show when both category and subcategory are selected */}
      {selectedCategory && selectedSubcategory && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Models Management
            </h2>
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {categories.find(cat => cat._id === selectedCategory)?.name} → {subcategories.find(sub => sub._id === selectedSubcategory)?.name}
              </p>
              <Button
                onClick={handleAddModel}
                variant="primary"
                size="md"
                startIcon={
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              >
                Add Model
              </Button>
            </div>
          </div>

          {modelLoading && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-600 dark:text-blue-400">Loading models...</p>
            </div>
          )}

          {modelError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{modelError}</p>
            </div>
          )}

          {models.length === 0 && !modelLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">No models found for this subcategory.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Model Selection Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Model
                </label>
                <select
                  value={selectedModel || ""}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full h-11 rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a model</option>
                  {models.map(model => (
                    <option key={model._id} value={model._id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Model Information Card */}
              {selectedModel && models.find(m => m._id === selectedModel) && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {(() => {
                    const model = models.find(m => m._id === selectedModel);
                    if (!model) return null;
                    
                    return (
                      <>
                        {/* Model Header */}
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                  Model Name
                                </label>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mt-1">
                                  {model.name}
                                </h3>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                    Shape
                                  </label>
                                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                                    {model.shape}
                                  </p>
                                </div>
                                
                                <div>
                                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                    Dimensions
                                  </label>
                                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                                    {model.width} × {model.depth} × {model.height}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <div className="text-right space-y-3">
                                <div>
                                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                    Price
                                  </label>
                                  <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                                    ${model.price || 0}
                                  </p>
                                </div>
                                
                                <div>
                                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                    Customizable
                                  </label>
                                  <div className="mt-1">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                      model.customizable 
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                                        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                    }`}>
                                      {model.customizable ? 'Yes' : 'No'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <Button
                                onClick={() => handleEditModel(model)}
                                variant="outline"
                                size="sm"
                                startIcon={
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="m18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                }
                              >
                                Edit Model
                              </Button>
                            </div>
                          </div>
                        </div>

                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Category Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-auto mt-0 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add New Category
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category Name
              </label>
              <Input
                type="text"
                placeholder="Enter category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                error={!!error}
                hint={error}
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitCategory}
                disabled={loading || !newCategoryName.trim()}
                loading={loading}
              >
                Add Category
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-auto mt-0 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Category
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category Name
              </label>
              <Input
                type="text"
                placeholder="Enter category name"
                value={editCategoryName}
                onChange={(e) => setEditCategoryName(e.target.value)}
                error={!!error}
                hint={error}
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseEditModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitEditCategory}
                disabled={loading || !editCategoryName.trim()}
                loading={loading}
              >
                Update Category
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Subcategory Modal */}
      {isSubcategoryModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-auto mt-0 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add New Subcategory
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Subcategory Name
              </label>
              <Input
                type="text"
                placeholder="Enter subcategory name"
                value={newSubcategoryName}
                onChange={(e) => setNewSubcategoryName(e.target.value)}
                error={!!subcategoryError}
                hint={subcategoryError}
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseSubcategoryModal}
                disabled={subcategoryLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitSubcategory}
                disabled={subcategoryLoading || !newSubcategoryName.trim()}
                loading={subcategoryLoading}
              >
                Add Subcategory
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Subcategory Modal */}
      {isSubcategoryEditModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-auto mt-0 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Subcategory
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Subcategory Name
              </label>
              <Input
                type="text"
                placeholder="Enter subcategory name"
                value={editSubcategoryName}
                onChange={(e) => setEditSubcategoryName(e.target.value)}
                error={!!subcategoryError}
                hint={subcategoryError}
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseSubcategoryEditModal}
                disabled={subcategoryLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitEditSubcategory}
                disabled={subcategoryLoading || !editSubcategoryName.trim()}
                loading={subcategoryLoading}
              >
                Update Subcategory
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Model Modal */}
      {isModelModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-auto mt-0 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add New Model
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter model name"
                  value={modelForm.name}
                  onChange={(e) => setModelForm({...modelForm, name: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Shape *
                </label>
                <Input
                  type="text"
                  placeholder="Enter shape"
                  value={modelForm.shape}
                  onChange={(e) => setModelForm({...modelForm, shape: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Height *
                </label>
                <Input
                  type="number"
                  placeholder="Enter height"
                  value={modelForm.height}
                  onChange={(e) => setModelForm({...modelForm, height: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Depth *
                </label>
                <Input
                  type="number"
                  placeholder="Enter depth"
                  value={modelForm.depth}
                  onChange={(e) => setModelForm({...modelForm, depth: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Width *
                </label>
                <Input
                  type="number"
                  placeholder="Enter width"
                  value={modelForm.width}
                  onChange={(e) => setModelForm({...modelForm, width: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model File
                </label>
                <input
                  type="file"
                  accept=".obj,.fbx,.gltf,.glb,.dae,.3ds,.blend"
                  onChange={(e) => setModelFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600"
                />
                {modelFile && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {modelFile.name}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Image 2D File
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImage2dFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600"
                />
                {image2dFile && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {image2dFile.name}
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Available Textures
                </label>
                
                {/* Texture Category Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Texture Category
                  </label>
                  <select
                    value={selectedTextureCategory}
                    onChange={(e) => setSelectedTextureCategory(e.target.value)}
                    className="w-full h-11 rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {textureCategories.map(category => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Texture Selection Grid */}
                <div className="max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {getTexturesByCategory(selectedTextureCategory).map((texture) => (
                      <div
                        key={texture._id}
                        className={`relative cursor-pointer rounded-lg border-2 transition-all duration-200 ${
                          modelForm.textureavailable.includes(texture._id)
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                        onClick={() => {
                          const currentTextures = modelForm.textureavailable;
                          const isSelected = currentTextures.includes(texture._id);
                          const newTextures = isSelected
                            ? currentTextures.filter(id => id !== texture._id)
                            : [...currentTextures, texture._id];
                          setModelForm({...modelForm, textureavailable: newTextures});
                        }}
                      >
                        <div className="aspect-square bg-gray-200 dark:bg-gray-600 rounded-lg overflow-hidden mb-2">
                          {texture.textureUrl ? (
                            <img
                              src={texture.textureUrl}
                              alt={texture.textureName || "Texture"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                            {texture.textureName || "Unnamed Texture"}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {texture.textureType}
                          </p>
                        </div>
                        
                        {/* Selection Indicator */}
                        {modelForm.textureavailable.includes(texture._id) && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {getTexturesByCategory(selectedTextureCategory).length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500 dark:text-gray-400">No textures available in this category.</p>
                    </div>
                  )}
                </div>
                
                {/* Selected Count */}
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {modelForm.textureavailable.length} texture(s) selected
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Available Colors
                </label>
                <Input
                  type="text"
                  placeholder="Comma separated colors"
                  value={modelForm.colorsavailable.join(", ")}
                  onChange={(e) => setModelForm({...modelForm, colorsavailable: e.target.value.split(",").map(c => c.trim()).filter(c => c)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price
                </label>
                <Input
                  type="number"
                  placeholder="Enter price"
                  value={modelForm.price}
                  onChange={(e) => setModelForm({...modelForm, price: e.target.value})}
                  step={0.01}
                  min="0"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="customizable"
                  checked={modelForm.customizable}
                  onChange={(e) => setModelForm({...modelForm, customizable: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="customizable" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Customizable
                </label>
              </div>
            </div>

            {/* Customizable Models Section */}
            {modelForm.customizable && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Customizable Models
                  </h4>
                  <Button
                    onClick={handleAddCustomizableModel}
                    variant="primary"
                    size="sm"
                    startIcon={
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 5V19M5 12H19"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    }
                  >
                    Add Model
                  </Button>
                </div>

                {customizableModels.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      No customizable models added yet. Click "Add Model" to add customizable variations.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customizableModels.map((model, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex-1">
                          <div className="flex items-center gap-4">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {model.name}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {model.shape} • {model.width} × {model.depth} × {model.height}
                              </p>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              ${model.price}
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleRemoveCustomizableModel(index)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                          startIcon={
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M18 6L6 18M6 6l12 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {modelError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{modelError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseModelModal}
                disabled={modelLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitModel}
                disabled={modelLoading || !modelForm.name.trim() || !modelForm.shape.trim() || !modelForm.height || !modelForm.depth || !modelForm.width}
                loading={modelLoading}
              >
                Add Model
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Model Modal */}
      {isModelEditModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-auto mt-0 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Model
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter model name"
                  value={modelForm.name}
                  onChange={(e) => setModelForm({...modelForm, name: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Shape *
                </label>
                <Input
                  type="text"
                  placeholder="Enter shape"
                  value={modelForm.shape}
                  onChange={(e) => setModelForm({...modelForm, shape: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Height *
                </label>
                <Input
                  type="number"
                  placeholder="Enter height"
                  value={modelForm.height}
                  onChange={(e) => setModelForm({...modelForm, height: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Depth *
                </label>
                <Input
                  type="number"
                  placeholder="Enter depth"
                  value={modelForm.depth}
                  onChange={(e) => setModelForm({...modelForm, depth: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Width *
                </label>
                <Input
                  type="number"
                  placeholder="Enter width"
                  value={modelForm.width}
                  onChange={(e) => setModelForm({...modelForm, width: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model URL
                </label>
                <Input
                  type="url"
                  placeholder="Enter model URL"
                  value={modelForm.model_url}
                  onChange={(e) => setModelForm({...modelForm, model_url: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Image 2D URL
                </label>
                <Input
                  type="url"
                  placeholder="Enter image URL"
                  value={modelForm.image2d_url}
                  onChange={(e) => setModelForm({...modelForm, image2d_url: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Available Textures
                </label>
                
                {/* Texture Category Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Texture Category
                  </label>
                  <select
                    value={selectedTextureCategory}
                    onChange={(e) => setSelectedTextureCategory(e.target.value)}
                    className="w-full h-11 rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {textureCategories.map(category => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Texture Selection Grid */}
                <div className="max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {getTexturesByCategory(selectedTextureCategory).map((texture) => (
                      <div
                        key={texture._id}
                        className={`relative cursor-pointer rounded-lg border-2 transition-all duration-200 ${
                          modelForm.textureavailable.includes(texture._id)
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                        onClick={() => {
                          const currentTextures = modelForm.textureavailable;
                          const isSelected = currentTextures.includes(texture._id);
                          const newTextures = isSelected
                            ? currentTextures.filter(id => id !== texture._id)
                            : [...currentTextures, texture._id];
                          setModelForm({...modelForm, textureavailable: newTextures});
                        }}
                      >
                        <div className="aspect-square bg-gray-200 dark:bg-gray-600 rounded-lg overflow-hidden mb-2">
                          {texture.textureUrl ? (
                            <img
                              src={texture.textureUrl}
                              alt={texture.textureName || "Texture"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                            {texture.textureName || "Unnamed Texture"}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {texture.textureType}
                          </p>
                        </div>
                        
                        {/* Selection Indicator */}
                        {modelForm.textureavailable.includes(texture._id) && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {getTexturesByCategory(selectedTextureCategory).length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500 dark:text-gray-400">No textures available in this category.</p>
                    </div>
                  )}
                </div>
                
                {/* Selected Count */}
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {modelForm.textureavailable.length} texture(s) selected
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Available Colors
                </label>
                <Input
                  type="text"
                  placeholder="Comma separated colors"
                  value={modelForm.colorsavailable.join(", ")}
                  onChange={(e) => setModelForm({...modelForm, colorsavailable: e.target.value.split(",").map(c => c.trim()).filter(c => c)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price
                </label>
                <Input
                  type="number"
                  placeholder="Enter price"
                  value={modelForm.price}
                  onChange={(e) => setModelForm({...modelForm, price: e.target.value})}
                  step={0.01}
                  min="0"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="customizable-edit"
                  checked={modelForm.customizable}
                  onChange={(e) => setModelForm({...modelForm, customizable: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="customizable-edit" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Customizable
                </label>
              </div>
            </div>

            {/* Customizable Models Section */}
            {modelForm.customizable && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Customizable Models
                  </h4>
                  <Button
                    onClick={handleAddCustomizableModel}
                    variant="primary"
                    size="sm"
                    startIcon={
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 5V19M5 12H19"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    }
                  >
                    Add Model
                  </Button>
                </div>

                {customizableModels.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      No customizable models added yet. Click "Add Model" to add customizable variations.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customizableModels.map((model, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-4 flex-1">
                          {/* 2D Image Display */}
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-600 flex-shrink-0">
                            {model.image2d_url ? (
                              <img
                                src={model.image2d_url}
                                alt={model.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-4">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {model.name}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {model.shape} • {model.width} × {model.depth} × {model.height}
                                </p>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                ${model.price}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => handleRemoveCustomizableModel(index)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                          startIcon={
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M18 6L6 18M6 6l12 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {modelError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{modelError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseModelEditModal}
                disabled={modelLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitEditModel}
                disabled={modelLoading || !modelForm.name.trim() || !modelForm.shape.trim() || !modelForm.height || !modelForm.depth || !modelForm.width}
                loading={modelLoading}
              >
                Update Model
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Customizable Model Modal */}
      {isCustomizableModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-auto mt-0 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add Customizable Model
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter model name"
                  value={customizableForm.name}
                  onChange={(e) => setCustomizableForm({...customizableForm, name: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Shape *
                </label>
                <Input
                  type="text"
                  placeholder="Enter shape"
                  value={customizableForm.shape}
                  onChange={(e) => setCustomizableForm({...customizableForm, shape: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Height *
                </label>
                <Input
                  type="number"
                  placeholder="Enter height"
                  value={customizableForm.height}
                  onChange={(e) => setCustomizableForm({...customizableForm, height: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Depth *
                </label>
                <Input
                  type="number"
                  placeholder="Enter depth"
                  value={customizableForm.depth}
                  onChange={(e) => setCustomizableForm({...customizableForm, depth: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Width *
                </label>
                <Input
                  type="number"
                  placeholder="Enter width"
                  value={customizableForm.width}
                  onChange={(e) => setCustomizableForm({...customizableForm, width: e.target.value})}
                  error={!!modelError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model File
                </label>
                <input
                  type="file"
                  accept=".obj,.fbx,.gltf,.glb,.dae,.3ds,.blend"
                  onChange={(e) => setCustomizableModelFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600"
                />
                {customizableModelFile && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {customizableModelFile.name}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Image 2D File
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCustomizableImage2dFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600"
                />
                {customizableImage2dFile && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Selected: {customizableImage2dFile.name}
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Available Textures
                </label>
                
                {/* Texture Category Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Texture Category
                  </label>
                  <select
                    value={selectedTextureCategory}
                    onChange={(e) => setSelectedTextureCategory(e.target.value)}
                    className="w-full h-11 rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {textureCategories.map(category => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Texture Selection Grid */}
                <div className="max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {getTexturesByCategory(selectedTextureCategory).map((texture) => (
                      <div
                        key={texture._id}
                        className={`relative cursor-pointer rounded-lg border-2 transition-all duration-200 ${
                          customizableForm.textureavailable.includes(texture._id)
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                        onClick={() => {
                          const currentTextures = customizableForm.textureavailable;
                          const isSelected = currentTextures.includes(texture._id);
                          const newTextures = isSelected
                            ? currentTextures.filter(id => id !== texture._id)
                            : [...currentTextures, texture._id];
                          setCustomizableForm({...customizableForm, textureavailable: newTextures});
                        }}
                      >
                        <div className="aspect-square bg-gray-200 dark:bg-gray-600 rounded-lg overflow-hidden mb-2">
                          {texture.textureUrl ? (
                            <img
                              src={texture.textureUrl}
                              alt={texture.textureName || "Texture"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                            {texture.textureName || "Unnamed Texture"}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {texture.textureType}
                          </p>
                        </div>
                        
                        {/* Selection Indicator */}
                        {customizableForm.textureavailable.includes(texture._id) && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {getTexturesByCategory(selectedTextureCategory).length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500 dark:text-gray-400">No textures available in this category.</p>
                    </div>
                  )}
                </div>
                
                {/* Selected Count */}
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {customizableForm.textureavailable.length} texture(s) selected
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Available Colors
                </label>
                <Input
                  type="text"
                  placeholder="Comma separated colors"
                  value={customizableForm.colorsavailable.join(", ")}
                  onChange={(e) => setCustomizableForm({...customizableForm, colorsavailable: e.target.value.split(",").map(c => c.trim()).filter(c => c)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price
                </label>
                <Input
                  type="number"
                  placeholder="Enter price"
                  value={customizableForm.price}
                  onChange={(e) => setCustomizableForm({...customizableForm, price: e.target.value})}
                  step={0.01}
                  min="0"
                />
              </div>
            </div>

            {modelError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{modelError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleCloseCustomizableModal}
                disabled={modelLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmitCustomizableModel}
                disabled={modelLoading || !customizableForm.name.trim() || !customizableForm.shape.trim() || !customizableForm.height || !customizableForm.depth || !customizableForm.width}
                loading={modelLoading}
              >
                Add Customizable Model
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelAdmin;
